'use client';

import { useState, useEffect } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [rateLimitWarning, setRateLimitWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/');
  const router = useRouter();

  // Get rate limit configuration from environment variables
  const maxLoginAttempts = parseInt(process.env.NEXT_PUBLIC_RATE_LIMIT_MAX_REQUESTS || process.env.NEXT_PUBLIC_RATE_LIMIT_MAX_AUTH || '10', 10);
  const rateLimitDuration = parseInt(process.env.NEXT_PUBLIC_RATE_LIMIT_DURATION || process.env.NEXT_PUBLIC_RATE_LIMIT_WINDOW_AUTH || '120', 10);

  // Check for redirect parameter in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = searchParams.get('redirect');
    if (redirect) {
      setRedirectPath(redirect);
    }
  }, []);

  // Reset failed attempts when component mounts or when error changes
  useEffect(() => {
    // Reset failed attempts when there's no error (successful login or fresh start)
    if (!error && !loading) {
      setFailedAttempts(0);
      setRateLimitWarning('');
    }
  }, [error, loading]);

  // Reset rate limit state when component mounts (fresh start)
  useEffect(() => {
    setFailedAttempts(0);
    setRateLimitWarning('');
    setError('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setRateLimitWarning('');

      // Add error handling for URL construction issues
      let result;
      try {
        result = await signIn('credentials', {
          redirect: false,
          email,
          password,
        });
      } catch (signInError) {
        console.error('SignIn error:', signInError);

        // Handle URL construction errors specifically
        if (signInError.message && signInError.message.includes('Invalid URL')) {
          setError('Configuration error. Please contact support or try again later.');
          return;
        }

        // Handle other NextAuth errors
        setError('Authentication service error. Please try again later.');
        return;
      }

      if (result.error) {

        // Try to parse the error message as JSON if it's a rate limit error
        let errorData = null;
        try {
          // Check if the error is a JSON string (which happens with rate limit errors)
          if (typeof result.error === 'string') {
            // First try to extract JSON from the error message if it contains JSON
            const jsonMatch = result.error.match(/\{.*\}/s);
            if (jsonMatch) {
              errorData = JSON.parse(jsonMatch[0]);
            }
            // If no JSON found but it's a rate limit error, create a basic error object
            else if (result.error.includes('TooManyRequests') ||
              result.error.includes('Too many login attempts') ||
              result.error.includes('temporarily blocked')) {
              errorData = {
                error: 'TooManyRequests',
                message: result.error
              };
            }
          }
        } catch (e) {
          // Error parsing JSON - continue with default error handling
        }

        // Check if the error message indicates a rate limit error
        if ((typeof result.error === 'string' && (
          result.error.includes('Too many login attempts') ||
          result.error.includes('TooManyRequests') ||
          result.error.includes('rate limit')
        )) ||
          (errorData && (
            errorData.error === 'TooManyRequests' ||
            (errorData.isRateLimited === true && !errorData.isBlocked)
          ))) {

          // Format the error message to be more user-friendly
          let retryAfter = '60';
          let rateLimitMessage = '';

          if (errorData && errorData.retryAfter) {
            retryAfter = errorData.retryAfter;
          } else {
            retryAfter = result.error.match(/(\d+)\s*seconds/)?.[1] || '60';
          }

          // Use the rateLimitMessage from the response if available
          if (errorData && errorData.rateLimitMessage) {
            rateLimitMessage = errorData.rateLimitMessage;
          } else if (errorData && errorData.message) {
            rateLimitMessage = errorData.message;
          } else {
            rateLimitMessage = `Too many login attempts. Please wait ${retryAfter} seconds before trying again.`;
          }
          setError(rateLimitMessage);

          // Set failed attempts if available
          if (errorData && errorData.failedAttempts) {
            setFailedAttempts(errorData.failedAttempts);
          } else {
            // If we don't have failed attempts from the server, increment our local counter
            setFailedAttempts(prevAttempts => prevAttempts + 1);
          }
        }
        // Check if the error message indicates a blocked account
        else if (
          (typeof result.error === 'string' && (
            result.error.includes('Your access has been temporarily blocked') ||
            result.error.includes('account is locked') ||
            result.error.includes('blocked due to')
          )) ||
          (errorData && (
            errorData.isBlocked === true ||
            (errorData.message && errorData.message.includes('Your access has been temporarily blocked'))
          ))
        ) {

          // Try to extract block time remaining from the response
          let blockTimeRemaining = 0;

          if (errorData && errorData.blockTimeRemaining) {
            blockTimeRemaining = errorData.blockTimeRemaining;
          }

          // If we have block time remaining, format a user-friendly message
          let blockMessage = '';

          // First try to use the rateLimitMessage from the response if available
          if (errorData && errorData.rateLimitMessage) {
            blockMessage = errorData.rateLimitMessage;
          }
          else if (blockTimeRemaining > 0) {
            // Convert milliseconds to more user-friendly units
            const seconds = Math.ceil(blockTimeRemaining / 1000);
            const minutes = Math.ceil(seconds / 60);
            const hours = Math.ceil(minutes / 60);
            const days = Math.ceil(hours / 24);

            // Create a user-friendly message based on the time remaining
            if (days > 1) {
              blockMessage = `Your access has been temporarily blocked due to too many failed login attempts. Please try again in ${days} days.`;
            } else if (hours > 1) {
              blockMessage = `Your access has been temporarily blocked due to too many failed login attempts. Please try again in ${hours} hours.`;
            } else if (minutes > 1) {
              blockMessage = `Your access has been temporarily blocked due to too many failed login attempts. Please try again in ${minutes} minutes.`;
            } else {
              blockMessage = `Your access has been temporarily blocked due to too many failed login attempts. Please try again in ${seconds} seconds.`;
            }
          } else {
            // If we don't have block time remaining, use the message from the response
            blockMessage = errorData && errorData.message ?
              errorData.message :
              'Your access has been temporarily blocked due to too many failed login attempts. Please try again later.';
          }
          setError(blockMessage);

          // Set failed attempts if available
          if (errorData && errorData.failedAttempts) {
            setFailedAttempts(errorData.failedAttempts);
          } else {
            // If we don't have failed attempts from the server, set a high number to indicate blocking
            setFailedAttempts(maxLoginAttempts);
          }
        }
        // Use a generic error message for credential issues
        else if (result.error.includes('No user found') || result.error.includes('Invalid password')) {
          setError('Login failed. Please check your credentials and try again.');

          // Get the actual remaining attempts from the server response if available
          let actualFailedAttempts = failedAttempts + 1;
          let actualRemainingAttempts = maxLoginAttempts - actualFailedAttempts;

          // Try to parse server response for actual rate limit data
          try {
            if (result.error && typeof result.error === 'string') {
              const jsonMatch = result.error.match(/\{.*\}/s);
              if (jsonMatch) {
                const errorData = JSON.parse(jsonMatch[0]);
                if (errorData.failedAttempts !== undefined) {
                  actualFailedAttempts = errorData.failedAttempts;
                  actualRemainingAttempts = maxLoginAttempts - actualFailedAttempts;
                }
              }
            }
          } catch (e) {
            // Could not parse server rate limit data, using local count
          }

          setFailedAttempts(actualFailedAttempts);

          // Show warning about remaining attempts
          if (actualRemainingAttempts > 0) {
            setRateLimitWarning(`Warning: You have ${actualRemainingAttempts} login attempts remaining before being temporarily blocked.`);
          } else if (actualRemainingAttempts === 0) {
            setRateLimitWarning('Warning: This is your last login attempt. Too many failed attempts will result in your account being temporarily locked.');
          } else {
            setRateLimitWarning('Your account has been temporarily locked due to too many failed login attempts.');
          }
        }
        else if (result.error.includes('Please provide both email and password')) {
          setError('Please enter both email and password.');
        }
        else {
          // Generic error message for login failure
          setError('Login failed. Please check your credentials and try again.');

          // Get the actual remaining attempts from the server response if available
          let actualFailedAttempts = failedAttempts + 1;
          let actualRemainingAttempts = maxLoginAttempts - actualFailedAttempts;

          // Try to parse server response for actual rate limit data
          try {
            if (result.error && typeof result.error === 'string') {
              const jsonMatch = result.error.match(/\{.*\}/s);
              if (jsonMatch) {
                const errorData = JSON.parse(jsonMatch[0]);
                if (errorData.failedAttempts !== undefined) {
                  actualFailedAttempts = errorData.failedAttempts;
                  actualRemainingAttempts = maxLoginAttempts - actualFailedAttempts;
                }
              }
            }
          } catch (e) {
            // Rate limit data parsing failed, using local count
          }

          setFailedAttempts(actualFailedAttempts);

          // Show warning about remaining attempts
          if (actualRemainingAttempts > 0) {
            setRateLimitWarning(`Warning: You have ${actualRemainingAttempts} login attempts remaining before being temporarily blocked.`);
          } else if (actualRemainingAttempts === 0) {
            setRateLimitWarning('Warning: This is your last login attempt. Too many failed attempts will result in your account being temporarily locked.');
          } else {
            setRateLimitWarning('Your account has been temporarily locked due to too many failed login attempts.');
          }
        }

        return;
      }

      // If we get here, login was successful
      clearForm(); // Clear the form and reset state
      router.push(redirectPath);
      router.refresh();
    } catch (error) {
      console.error('Login error:', error);

      // Handle specific error types
      if (error.message && error.message.includes('Invalid URL')) {
        setError('Configuration error. Please contact support or try again later.');
      } else if (error.message && error.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const clearForm = () => {
    setEmail('');
    setPassword('');
    setError('');
    setRateLimitWarning('');
    setFailedAttempts(0);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg border border-gray-200" style={{ boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', background: 'linear-gradient(to right, #f9f9f9, #ffffff, #f9f9f9)', backgroundImage: "url('data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23f0f0f0' fill-opacity='0.2' fill-rule='evenodd'/%3E%3C/svg%3E')" }}>
        <div className="flex justify-center mb-6">
          <Image
            src="/images/Logo.png"
            alt="Logo"
            width={150}
            height={150}
            priority
          />
        </div>
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link href="/register" className="font-medium text-blue-600 hover:text-blue-500">
              create a new account
            </Link>
          </p>
        </div>

        {/* Rate Limit Information Display */}


        {(rateLimitWarning || (failedAttempts > 0 && !error)) && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  {rateLimitWarning || `Warning: You have ${failedAttempts} failed login attempts. Too many failed attempts may result in your account being temporarily locked.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 shadow-md">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-red-500 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-medium text-red-800 text-sm">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <div className="text-sm">
              <Link href="/forget-password" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
